import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const GlossaryAssignment = ({ selectedWords, glossaryTerms, onAssignGlossary, onCreateNew }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTerm, setNewTerm] = useState({ word: '', definition: '', pronunciation: '' });

  const filteredTerms = glossaryTerms?.filter(term =>
    term?.word?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const glossaryOptions = filteredTerms?.map(term => ({
    value: term?.id,
    label: term?.word,
    description: term?.definition?.substring(0, 60) + '...'
  }));

  const handleAssign = () => {
    if (selectedTerm && selectedWords?.length > 0) {
      onAssignGlossary(selectedWords, selectedTerm);
      setSelectedTerm('');
    }
  };

  const handleCreateNew = () => {
    if (newTerm?.word && newTerm?.definition) {
      onCreateNew(newTerm);
      setNewTerm({ word: '', definition: '', pronunciation: '' });
      setShowCreateForm(false);
    }
  };

  return (
    <div className="p-6 bg-background border-t border-border">
      <div className="flex items-start gap-3 mb-4">
        <Icon name="BookText" size={20} className="text-primary mt-1" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground mb-1">
            Glossary Assignment
          </h3>
          <p className="text-xs text-muted-foreground">
            Assign selected words to glossary definitions
          </p>
        </div>
      </div>
      {selectedWords?.length === 0 ? (
        <div className="bg-muted/50 border border-border rounded-lg p-6 text-center">
          <Icon name="Info" size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Select words from the line above to assign glossary definitions
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm font-medium text-foreground mb-3">
              Selected Words ({selectedWords?.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedWords?.map((word, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-secondary/20 text-secondary-foreground text-sm rounded-full border border-secondary/30"
                >
                  {word?.split('-')?.[0]}
                </span>
              ))}
            </div>
          </div>

          {!showCreateForm ? (
            <div className="bg-card border border-border rounded-lg p-4">
              <Select
                label="Select Glossary Term"
                placeholder="Search and select a term"
                searchable
                options={glossaryOptions}
                value={selectedTerm}
                onChange={setSelectedTerm}
                className="mb-4"
              />

              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAssign}
                  disabled={!selectedTerm}
                  iconName="Link"
                  iconPosition="left"
                >
                  Assign to Selected Words
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreateForm(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Create New Term
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-foreground">Create New Glossary Term</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreateForm(false)}
                  iconName="X"
                  iconSize={16}
                >
                  Cancel
                </Button>
              </div>

              <div className="space-y-3">
                <Input
                  label="Word"
                  type="text"
                  placeholder="Enter the word"
                  value={newTerm?.word}
                  onChange={(e) => setNewTerm({ ...newTerm, word: e?.target?.value })}
                  required
                />
                <Input
                  label="Definition"
                  type="text"
                  placeholder="Enter the definition"
                  value={newTerm?.definition}
                  onChange={(e) => setNewTerm({ ...newTerm, definition: e?.target?.value })}
                  required
                />
                <Input
                  label="Pronunciation (Optional)"
                  type="text"
                  placeholder="e.g., /ˈθaʊ/"
                  value={newTerm?.pronunciation}
                  onChange={(e) => setNewTerm({ ...newTerm, pronunciation: e?.target?.value })}
                />
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleCreateNew}
                  disabled={!newTerm?.word || !newTerm?.definition}
                  iconName="Save"
                  iconPosition="left"
                  fullWidth
                >
                  Create and Assign
                </Button>
              </div>
            </div>
          )}

          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Lightbulb" size={20} className="text-accent mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Archaic Words Detected</p>
                <p className="text-xs text-muted-foreground mb-2">
                  The following words may need glossary definitions:
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-warning/20 text-warning-foreground text-xs rounded border border-warning/30">
                    thou
                  </span>
                  <span className="px-2 py-1 bg-warning/20 text-warning-foreground text-xs rounded border border-warning/30">
                    thee
                  </span>
                  <span className="px-2 py-1 bg-warning/20 text-warning-foreground text-xs rounded border border-warning/30">
                    wherefore
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlossaryAssignment;